import { useState, useEffect } from "react";
import axios from "axios";

export const useAiRoutes = (selectedExtension) => {
  const [aiRoutes, setAiRoutes] = useState([]);
  const [reportRoutes, setReportRoutes] = useState([]);
  const [channelRoutes, setChannelRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extensionInfo, setExtensionInfo] = useState(null);
  const [examDashboardData, setExamDashboardData] = useState({});
  const [skillBridgeData, setSkillBridgeData] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchAiRoutes = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_EP_URL}/api/aiStudio`
        );

        if (response.status === 200) {
          const excludedTypes = [
            "attendance",
            "assignment",
            "dashboard",
            "whiteBoard",
            "events",
            "booking",
            "feedback",
            "aiInsights",
            "generateAvatar",
            "skillBridge",
            "examDashboard",
          ];

          const reportTypes = ["report", "reports"];

          const channel = ["channel", "channels"];

          const allData = response.data.data || response.data;

          const examData = allData?.find(
            (item) => item.type === "examDashboard"
          );
          setExamDashboardData(examData);

          const skillbridgeData = allData?.find(
            (item) => item.type === "skillBridge"
          );
          setSkillBridgeData(skillbridgeData);

          // Find the first enabled extension that's not in excluded types
          const enabledExtension = Object.entries(selectedExtension || {}).find(
            ([key, value]) =>
              !excludedTypes.includes(key) &&
              (value === true || value === "true")
          );

          if (enabledExtension && isMounted) {
            const [extensionType, isEnabled] = enabledExtension;

            // Find the extension info from the API response
            const extensionData = allData.find(
              (item) => item.type === extensionType
            );

            if (extensionData) {
              setExtensionInfo({
                type: extensionType,
                name:
                  extensionData.name ||
                  extensionType.charAt(0).toUpperCase() +
                    extensionType.slice(1),
                isEnabled: isEnabled,
              });
            } else {
              // If not found in API, create a default entry
              setExtensionInfo({
                type: extensionType,
                name:
                  extensionType.charAt(0).toUpperCase() +
                  extensionType.slice(1),
                isEnabled: isEnabled,
              });
            }
          }

          const filteredData = allData.filter((item) => {
            // Check if type is in excluded types
            if (excludedTypes.includes(item.type)) {
              return false;
            }

            // Check if name contains any report types
            const itemName = item.type ? item.type.toLowerCase() : "";
            const hasReportType = reportTypes.some((reportType) =>
              itemName.includes(reportType.toLowerCase())
            );
            // Check if name contains any channel types
            const hasChannelType = channel.some((channelType) =>
              itemName.includes(channelType.toLowerCase())
            );

            if (hasReportType || hasChannelType) {
              return false;
            }

            // Check if extension is enabled
            return (
              selectedExtension[item.type] === true ||
              selectedExtension[item.type] === "true"
            );
          });

          // Filter report routes
          const reportData = allData.filter((item) => {
            // Check if name contains any report types
            const itemName = item.type ? item.type.toLowerCase() : "";
            const hasReportType = reportTypes.some((reportType) =>
              itemName.includes(reportType.toLowerCase())
            );

            // Check if extension is enabled
            return (
              hasReportType &&
              (selectedExtension[item.type] === true ||
                selectedExtension[item.type] === "true")
            );
          });

          // Filter channel routes
          const channelData = allData.filter((item) => {
            // Check if name contains any channel types
            const itemName = item.name ? item.name.toLowerCase() : "";
            const hasChannelType = channel.some((channelType) =>
              itemName.includes(channelType.toLowerCase())
            );

            // Check if extension is enabled
            return (
              hasChannelType &&
              (selectedExtension[item.type] === true ||
                selectedExtension[item.type] === "true")
            );
          });

          // Only update state if component is still mounted
          if (isMounted) {
            setAiRoutes(filteredData);
            setReportRoutes(reportData);
            setChannelRoutes(channelData);
          }
        } else {
          if (isMounted) {
            setError("Failed to fetch AI routes");
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAiRoutes();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [selectedExtension]); // Include this if selectedExtension can change

  return {
    aiRoutes,
    reportRoutes,
    channelRoutes,
    loading,
    error,
    extensionInfo,
    examDashboardData,
    skillBridgeData,
  };
};
